import { Component, OnInit, Output, EventEmitter, ViewChild, OnDestroy, inject, Input, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, Observable, take, takeUntil, Subject, tap, switchMap, pipe, forkJoin } from 'rxjs';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { Location } from '@angular/common';
import * as svgIcons from '@progress/kendo-svg-icons';
import { State } from '@progress/kendo-data-query';

import { TicketHeaderService } from '../ticket-header.service';
import { TicketHeader } from '../ticket-header.model';
import { TicketDetailService } from '../../ticket-detail/ticket-detail.service';
import { TicketDetail } from '../../ticket-detail/ticket-detail.model';
import { UtilService } from 'src/app/shared/util.service';
import { CustomerService } from 'src/app/module/account-receivable/customer/customer.service';
import { GridService } from 'src/app/shared/grid.service';
import { TicketEditConfiguration } from '../../../shared/ticket-edit-configuration.model';
import { ActivityCode } from '../../../shared/activity-code.model';
import { Technician } from '../../../shared/technician.model';
import { InvoicingCategory } from '../../../invoicing-category/invoicing-category.model';
import { ServiceSharedService } from '../../../shared/service-shared-services';
import { DynamicEntityService } from 'src/app/module/servicentre/dynamic-entity.service';
import { TicketTypeService } from '../../../ticket-type/ticket-type.service';
import { TicketStatusService } from '../../../ticket-status/ticket-status.service';
import { ServiceContractService } from 'src/app/module/account-receivable/service-contract/service-contract.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { appUser } from 'src/app/module/security/user/user.model';
import { UserService } from 'src/app/module/security/user/user.service';
import { PriorityCode } from '../../../shared/priority-model';
//import PriorityListJson from 'src/assets/data/ticket-header-priority.json';

@Component({
  selector: 'app-ticket-header-edit',
  templateUrl: './ticket-header-edit.component.html',
  styleUrls: ['./ticket-header-edit.component.scss'],
})
export class TicketHeaderEditComponent implements OnInit, OnDestroy {
  private ticketHeaderService = inject(TicketHeaderService);
  private customerService = inject(CustomerService);
  private ticketDetailService = inject(TicketDetailService);
  private serviceSharedService = inject(ServiceSharedService);
  private dynamicEntityService = inject(DynamicEntityService);
  private utilService = inject(UtilService);
  private ticketTypeService = inject(TicketTypeService);
  private ticketStatusService = inject(TicketStatusService);
  private serviceContractService = inject(ServiceContractService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  public allIcons = svgIcons;

  //quand un parent veut passer un ticket en mode "ajout" (pour un nouveau ticket)
  @Input() newTicketDetail!: TicketDetail;
  @Input() public ticketEditConfiguration: TicketEditConfiguration = new TicketEditConfiguration();

  @Output() closeEditPanel = new EventEmitter();
  @Output() refreshFdtList = new EventEmitter(); // pour component TICKET-FDT-LIST
  @Output() refreshList = new EventEmitter(); // pour component TICKET-HEADER-LIST
  @Output() updateFormDirtyState = new EventEmitter();
  @ViewChild(NgForm) editform!: NgForm;

  customerName = '';
  ticketHeader: TicketHeader = new TicketHeader();
  isLoading = false;
  isChangeEmitted = false;
  ticketDetailList: TicketDetail[] = [];
  scTechnicianCode: any;
  ticketDetailDate: any;
  ticketDetailDateStart: any;
  ticketDetailDateEnd: any;
  activityCodeList: ActivityCode[] = [];
  localisationList: string[] = [];
  public totalHours: number = 0;
  private unsubNotifier = new Subject<void>();
  priorityList: PriorityCode[] = [];
  private promptToRecalculateWork = false;

  technicianList: Technician[] = [];
  public technicianFilterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };
  public ticketTypeList: any;
  public ticketTypeFilterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };
  public ticketStatusList: any;
  public ticketStatusFilterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };
  invoicingCategoryList: InvoicingCategory[] = [];
  public invoicingCategoryFilterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  constructor(private activatedRoute: ActivatedRoute, private location: Location) {
    this.ticketHeaderService.initOne();

    // subscribe to data ticket "header"
    this.ticketHeaderService.ticket.pipe(takeUntil(this.unsubNotifier)).subscribe((response) => {
      if (response) {
        // set data
        this.ticketHeader = response;
        this.ticketHeader.openingDate = this.ticketHeader.openingDate.split('T')[0];
        this.totalHours = response.totalWorkedHours + response.totalTravelledHours;
      }
      // reset form
      this.isChangeEmitted = false;
      if (this.editform) this.editform.control.markAsPristine();
      // si un ticket est sélectionné
      if (this.ticketHeader.ticketId != 0) {
        // get customer name
        this.customerService
          .listCache()
          .pipe(takeUntil(this.unsubNotifier))
          .subscribe(() => {
            this.customerName = this.customerService.getCustomerName(this.ticketHeader.customerCode);
          });
        this.refreshTicketDetailList();
      }
    });

    // fetch data
    forkJoin({
      activityCodeList: this.serviceSharedService.getListOfActivityCode(),
      invoicingCategoryList: this.dynamicEntityService.getListOfInvoicingCategory(false),
      technicianList: this.serviceSharedService.getListOfTechnician(),
      ticketTypeList: this.ticketTypeService.listCache(),
      ticketStatusList: this.ticketStatusService.listCache(),
      localisationList: this.serviceSharedService.getListOfLocalisation(),
      priorityList: this.serviceSharedService.getListOfPriorityCode(false),
    }).subscribe((response: any) => {
      this.isLoading = false;
      this.activityCodeList = response.activityCodeList;
      this.localisationList = response.localisationList;
      this.invoicingCategoryList = response.invoicingCategoryList;
      this.priorityList = response.priorityList;
      // seulement garder les techniciens actifs
      this.technicianList = response.technicianList.filter((item: any) => item.IsActive === true && item.TechnicianCode !== 'CONTRAT');
      // ticket type pour filtre dropdown
      this.ticketTypeList = response.ticketTypeList;
      // ticket status pour filtre dropdown
      this.ticketStatusList = response.ticketStatusList;
      this.ticketStatusList.push({ ServicentreTextualCode: 'FACTURE' }); // Dangereux, ca n'existe pas dans le backend, pas de Id d'associer
      this.ticketStatusList.sort((a: any, b: any) => {
        if (a.ServicentreTextualCode < b.ServicentreTextualCode) return -1;
        if (a.ServicentreTextualCode > b.ServicentreTextualCode) return 1;
        return 0;
      });
    });
  }

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(
        catchError((err: any) => {
          this.isLoading = false;
          throw err;
        }),
        takeUntil(this.unsubNotifier)
      )
      .subscribe((response: any) => {
        if (response!.ticket) {
          this.isLoading = false;
          this.ticketHeader = response.ticket;
        }
      });
  }

  getCurrentUserCode() {
    let currentUser: appUser = this.authService.GetCurrentUser();
    let userEmail = currentUser?.email;
    let user = this.technicianList.find((x) => x.Email == userEmail);
    if (user != null) {
      return user.TechnicianCode;
    }
    return '';
  }

  /*
   * Ajoute un nouveau ticket
   */
  addTicketHeader() {
    // get current user code
    let currentUserCode = this.getCurrentUserCode();
    // init data
    this.ticketHeader = new TicketHeader();
    this.ticketHeader.scTicketStatusCode = 'NOUVEAU';
    this.ticketHeader.sC_Ticket_CreatedBy_UserCode = currentUserCode;
    this.ticketHeader.sC_Ticket_AssignedTo_UserCode = currentUserCode;
    this.ticketHeader.openingDate = new Date().toISOString().split('T')[0];
    this.ticketHeader.scPriorityId = -1;
    this.ticketDetailList = [];
    // load user preferences
    this.ticketHeader.scInvoicingCategoryCode = this.userService.getUserPreference('ticket_header_scInvoicingCategoryCode');
    this.ticketHeader.scTicketType = this.userService.getUserPreference('ticket_header_scTicketType');
    this.ticketHeader.scLocalisation = this.userService.getUserPreference('ticket_header_scLocalisation');
    // émettre un changement pour désactiver la grille
    this.isChangeEmitted = true;
    this.updateFormDirtyState.emit(true);
    // sélection du client
    this.selectCustomer();

    /*
    texte popup changement client: "Le changement de client peut avoir un effet sur la tarification de l'appel, Voulez-vous recalculer les travaux de l'Appel maintenant?"
    - Faites "OUI" pour recalculer les travaux
    - Faites "NON" pour changer le client sans recalculer les travaux
    - Faites "ANNULER" pour annuler le changement de client
    */

    /*
    Texte popup de changement de client qui possede des contrats de services
    "Voulez-vous que l'appel de service passe sur le contrat de service?" oui/non
    action oui: afficher la liste des contrats de services du client
    */

    // TMP
  }

  /*
   * Rafraichit la liste des commentaires du ticket
   */
  refreshTicketDetailList() {
    // read url param to get ticket details
    this.scTechnicianCode = this.utilService.getUrlParameter('scTechnicianCode');

    this.gererDateDebutFin();

    // build odata query string
    var oDataQuery = `filter=TicketId eq ${this.ticketHeader.ticketId}`;
    if (this.scTechnicianCode) oDataQuery += ` and scTechnicianCode eq '${this.scTechnicianCode}'`;

    //gestion de la date
    if (this.ticketEditConfiguration.minTicketDetailDate.toISOString() === this.ticketEditConfiguration.maxTicketDetailDate.toISOString()) {
      oDataQuery += ` and TicketDetailDate eq ${this.ticketDetailDate}`;
    } else if (this.ticketEditConfiguration.maxTicketDetailDate.getFullYear() < 2099) {
      oDataQuery += ` and TicketDetailDate ge ${this.ticketDetailDateStart} and TicketDetailDate le ${this.ticketDetailDateEnd}`;
    }
    oDataQuery += `&orderby=TicketDetailDate desc, lineNumber desc`;
    // fetch ticket detail data
    this.isLoading = true;
    this.ticketDetailService
      .odataStr({}, oDataQuery)
      .pipe(takeUntil(this.unsubNotifier))
      .subscribe((detail) => {
        this.isLoading = false;
        this.ticketDetailList = detail.value;
      });
  }

  /*
   * Rafraichit le header du ticket, permet de rafraichir le Total d'heures travaillées (unique utilité pour le moment)
   */
  refreshTicketHeader() {
    this.ticketHeaderService
      .read(this.ticketHeader.ticketId)
      .pipe(takeUntil(this.unsubNotifier))
      .subscribe((response) => {});
  }

  ngOnDestroy(): void {
    this.unsubNotifier.next();
    this.unsubNotifier.unsubscribe();
  }

  /*
   * Cette fonction refresh tout les composants de l'écran (header, liste des commentaires, feuille de temps)
   */
  refreshScreenData() {
    // refresh TOTAL HRS
    this.refreshTicketHeader();
    // refresh la liste des commentaires
    this.refreshTicketDetailList();
    // refresh la feuille de temps (totaux)
    this.refreshFdtList.emit();
  }

  ngDoCheck(changes: any) {
    if (!this.isChangeEmitted && this.editform?.pristine === false) {
      this.isChangeEmitted = true;
      this.updateFormDirtyState.emit(true);
    }
  }

  onSubmit(form: NgForm) {
    this.isLoading = true;
    if (this.ticketHeader.ticketId) {
      // UPDATE

      /*
      // ON HOLD -> PHASE B
      // prompt to recalculate work
      if (this.promptToRecalculateWork) {
        this.utilService
          .confirm(
            'module.ticket.popup_confirm_recalculate_work.title',
            'module.ticket.popup_confirm_recalculate_work.message',
            'module.ticket.popup_confirm_recalculate_work.yes',
            'module.ticket.popup_confirm_recalculate_work.no'
          )
          .subscribe((approved: boolean) => {
            if (approved) {
            // calculer les travaux
            } else {
            // ne pas recalculer les travaux
            }
          });
      } else {
        // pas besoin de recalculer les travaux
      }
        */

      // preparation du data
      let postData = this.preparePostData();
      // update call
      this.ticketHeaderService
        .update(postData)
        .pipe(
          catchError((error: any) => {
            this.isLoading = false;
            this.utilService.notify(error.key, 'error');
            throw error;
          })
        )
        .pipe(takeUntil(this.unsubNotifier))
        .subscribe((response) => {
          this.isLoading = false;
          this.isChangeEmitted = false;
          this.editform.control.markAsPristine();
          this.ticketHeader = response;
          this.refreshFdtList.emit();
          this.refreshList.emit();
          this.updateFormDirtyState.emit(false);
          this.utilService.notify('shared.notification.success_update', 'success');
        });
    } else {
      // CREATE

      // preparation du data
      let postData = this.preparePostData();
      // create call
      this.ticketHeaderService
        .create(postData)
        .pipe(
          catchError((error: any) => {
            this.isLoading = false;
            throw error;
          })
        )
        .pipe(takeUntil(this.unsubNotifier))
        .subscribe((response) => {
          this.isLoading = false;
          this.location.replaceState('ticket/' + response.ticketId);
          this.ticketHeader = response;
          this.isChangeEmitted = false;
          this.editform.control.markAsPristine();
          this.refreshFdtList.emit();
          this.refreshList.emit();
          this.updateFormDirtyState.emit(false);
          this.utilService.notify('shared.notification.success_create', 'success');
        });
    }
  }

  /**
   * Cette fonction sert a uniformiser le data envoyé lors d'un create ou un update
   * et permet d'exclure tous les champs insignifiants du payload
   * @returns le data pour un create ou un update de la table usager
   */
  preparePostData(): any {
    let postData = {
      ticketId: this.ticketHeader.ticketId,
      customerCode: this.ticketHeader.customerCode,
      scTicketStatusCode: this.ticketHeader.scTicketStatusCode,
      scPriorityId: this.ticketHeader.scPriorityId,
      scInvoicingCategoryCode: this.ticketHeader.scInvoicingCategoryCode,
      scTicketType: this.ticketHeader.scTicketType,
      scLocalisation: this.ticketHeader.scLocalisation,
      sC_Ticket_AssignedTo_UserCode: this.ticketHeader.sC_Ticket_AssignedTo_UserCode,
      sC_Ticket_CreatedBy_UserCode: this.ticketHeader.sC_Ticket_CreatedBy_UserCode,
      customerContactName: this.ticketHeader.customerContactName,
      customerContactPhone: this.ticketHeader.customerContactPhone,
      scTicketShortTitle: this.ticketHeader.scTicketShortTitle.toUpperCase(),
      scTicketPONumber: this.ticketHeader.scTicketPONumber,
      scGlobalComment: this.ticketHeader.scGlobalComment,
      serviceContractId: this.ticketHeader.serviceContractId,
    };
    return postData;
  }

  fermer() {
    this.popConfirmExit().subscribe((approved) => {
      if (approved) this.annuler();
    });
  }

  public popConfirmExit(): Observable<boolean> {
    const confirmObservable = new Observable<boolean>((observer) => {
      if (!this.editform.pristine) {
        this.utilService
          .confirm(
            'shared.edit.popup_form_changed.title',
            'shared.edit.popup_form_changed.message',
            'shared.edit.popup_form_changed.yes',
            'shared.edit.popup_form_changed.no'
          )
          .subscribe((approved: boolean) => {
            observer.next(approved);
            observer.complete();
          });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
    return confirmObservable;
  }

  annuler() {
    // update url
    let path = this.location.path();
    if (path.substring(0, 5) === '/fdt/') this.location.replaceState('fdt/');
    else this.location.replaceState('ticket/');
    // reset form and close panel
    this.editform.control.markAsPristine();
    this.closeEditPanel.emit();
  }

  /*
   * Cette fonction lit les paramètres de l'URL pour déterminer les dates de début et de fin affiché dans laliste des commentaires.
   */
  gererDateDebutFin() {
    this.ticketDetailDate = this.utilService.getUrlParameter('ticketDetailDate');
    this.ticketDetailDateStart = this.utilService.getUrlParameter('ticketDetailDateStart');
    this.ticketDetailDateEnd = this.utilService.getUrlParameter('ticketDetailDateEnd');

    //une seule date
    if (this.ticketDetailDate) {
      this.ticketEditConfiguration.minTicketDetailDate = this.utilService.formatStringToDate(this.ticketDetailDate);
      this.ticketEditConfiguration.maxTicketDetailDate = this.utilService.formatStringToDate(this.ticketDetailDate);
    }
    //un RANGE de date
    else if (this.ticketDetailDateStart && this.ticketDetailDateEnd) {
      this.ticketEditConfiguration.minTicketDetailDate = this.utilService.formatStringToDate(this.ticketDetailDateStart);
      this.ticketEditConfiguration.maxTicketDetailDate = this.utilService.formatStringToDate(this.ticketDetailDateEnd);
    } // aucune limitation de date
    else {
      this.ticketEditConfiguration.minTicketDetailDate = new Date(1900, 1, 1);
      this.ticketEditConfiguration.maxTicketDetailDate = new Date(2099, 12, 31);
    }
  }

  //**Bouton qui déclenche l'ajout d'un commentaire.  on va INITIER un TicketDetail ici, que le composant Ticket-Edit pourra poursuivre. */
  public addComment(newTicketDetailData?: TicketDetail) {
    //NOTE 2024-06-25 : ça marche mais on devrait pouvoir utiliser newTicketDetailData pour créer le data
    // la ca marche parce qu'une partie du data est setter dans l'URL (date + operateur)
    // ticketid est bon parce que ticket header edit l'initalise

    // set default values
    let newTicketDetail = new TicketDetail();
    newTicketDetail.scInvoicingCategoryCode = this.ticketHeader.scInvoicingCategoryCode;
    newTicketDetail.ticketId = this.ticketHeader.ticketId;
    newTicketDetail.scTechnicianCode = this.scTechnicianCode;
    if (newTicketDetail.scTechnicianCode == '' || newTicketDetail.scTechnicianCode == null) {
      newTicketDetail.scTechnicianCode = this.ticketHeader.sC_Ticket_CreatedBy_UserCode;
    }

    this.gererDateDebutFin();

    let DateToUse = new Date().toISOString();
    //si la date de fin est plus basse que getFullYear, alors c'est soit un range, soit une date unique...auxquel cas, on veut prednre cette date
    if (this.ticketEditConfiguration.maxTicketDetailDate.getFullYear() < 2099) {
      DateToUse = this.ticketEditConfiguration.maxTicketDetailDate.toISOString();
    }

    if (DateToUse == '' || DateToUse == null) {
      DateToUse = new Date().toISOString();
    }

    let date: Date = this.utilService.formatStringToDate(DateToUse);
    newTicketDetail.ticketDetailDate = date;

    this.ticketDetailService.popupEdit(newTicketDetail).subscribe((result: any) => {
      if (result !== false) {
        this.refreshScreenData();
      }
    });
  }

  showAllComments() {
    this.location.replaceState('fdt/' + this.ticketHeader.ticketId);
    this.refreshTicketDetailList();
  }

  /*
   * refresh la feuille de temps quand on modifie une entrée de temps
   */
  refreshFdtListComponent() {
    this.isLoading = false;
    this.refreshFdtList.emit();
  }

  isLoadingChange(event: any) {
    this.isLoading = event;
  }

  /*
   * Cette fonction est appellée par le composant EDIT, si un un commentaire est en mode edit, on bloque la grille.
   */
  public updateEditFormDirtyState(event: any) {
    this.updateFormDirtyState.emit(event);
  }

  /*
   * Enlève un commentaire de la liste des commentaires.
   */
  removeCommentaire(ticketDetail: TicketDetail) {
    this.ticketDetailList = this.ticketDetailList.filter((item) => item.ticketId !== ticketDetail.ticketId && item.lineNumber !== ticketDetail.lineNumber);
  }

  /*
   * Affiche un popup de selection de client
   */
  selectCustomer() {
    this.customerService.popup().subscribe((customer: any) => {
      if (customer) {
        this.editform.control.markAsDirty();
        this.customerName = customer.noClient + ' - ' + customer.nomClient;
        this.ticketHeader.customerCode = customer.noClient;
        this.selectContract(customer.noClient);
      }
    });
  }

  /*
   *Affiche un popup de selection de contrat
   */
  selectContract(customerCode: string) {
    this.serviceContractService.odataStr({}, "filter=CustomerCode eq '" + customerCode + "'").subscribe((response: any) => {
      if (response.value.length > 0) {
        // le client a des contrats de service, on demande si on veut les utiliser
        this.utilService
          .confirm(
            'module.ticket.popup_confirm_contract.title',
            'module.ticket.popup_confirm_contract.message',
            'module.ticket.popup_confirm_contract.yes',
            'shared.edit.popup_form_changed.no'
          )
          .subscribe((approved: boolean) => {
            console.log('approved?', approved);
            if (approved) {
              // afficher la liste des contrats
              this.serviceContractService.popup(customerCode).subscribe((response: any) => {
                console.log('selected contract', response);
                if (response) {
                  // set contract
                  this.ticketHeader.serviceContractId = response.serviceContractId;
                  // set flag to prompt to recalculate work
                  this.promptToRecalculateWork = true;
                }
              });
            }
          });
      } else {
        // le client n'a pas de contrat de service
      }
    });
  }

  openPresetPopup() {
    this.ticketHeaderService.popupUserPreset('1');
  }
}
